import {
  OperationData,
  UpdateExpr,
  UpdateRel,
  UpdateDocument,
  UpdateAgent,
  UpdateOrReplaceColumn,
} from './schema';
import { Assert } from '@cotera/utilities';
import { Graph } from '../graph';
import _ from 'lodash';
import { DocumentEditor } from '../document';
import { EraQL } from '@cotera/era';
import { BuildExplorer } from './build-explorer';

export const renameSym = (params: {
  from: string;
  to: string;
  graph: Record<string, Graph.Resolution>;
}): OperationData[] => {
  const { from, to, graph } = params;

  // Create a BuildExplorer from the graph for dependency checking
  // We need to extract the raw nodes from the graph to create the BuildExplorer
  const graphExplorer = BuildExplorer.fromGraph(graph);

  const renameOp: OperationData = {
    t: 'rename',
    symbol: from,
    newSymbol: to,
  };

  const additionalOps: OperationData[] = [];

  for (const [symbol, { item, deps }] of Object.entries(graph)) {
    const affected = deps[from];
    if (affected && affected.length > 0) {
      switch (item.t) {
        case 'rel': {
          additionalOps.push(
            relUpdate({ to, from, symbol, node: item.relNode, deps: affected })
          );

          break;
        }
        case 'expr':
          additionalOps.push(
            exprUpdate({
              to,
              from,
              symbol,
              node: item.exprNode,
              deps: affected,
            })
          );
          break;
        case 'document':
          additionalOps.push(
            documentUpdate({
              to,
              from,
              symbol,
              node: item.documentNode,
              deps: affected,
            })
          );
          break;
        case 'agent':
          additionalOps.push(
            agentUpdate({
              to,
              from,
              symbol,
              node: item.agentNode,
              deps: affected,
            })
          );
          break;
        case 'tool':
          // Tools don't have dependencies to update
          break;
        default:
          return Assert.unreachable(item);
      }
    }
  }

  // Also check all rel nodes for LLM v2 columns that reference the renamed symbol
  // (even if the rel itself doesn't depend on it)
  for (const [symbol, { item }] of Object.entries(graph)) {
    if (item.t === 'rel') {
      const llmColUpdates = findAndUpdateLlmV2Columns({
        relSymbol: symbol,
        relNode: item.relNode,
        from,
        to,
        isColumnRename: false,
        graphExplorer,
      });
      additionalOps.push(...llmColUpdates);
    }
  }

  return [renameOp, ...additionalOps];
};

const exprUpdate = (params: {
  from: string;
  to: string;
  symbol: string;
  node: Graph.ExprNode;
  deps: readonly Graph.GraphDep[];
}): UpdateExpr => {
  const { from, to, symbol, node, deps } = params;
  const update: UpdateExpr = {
    symbol,
    t: 'update-expr',
  };

  let remaining = deps;

  const [source, rest1] = _.partition(
    remaining,
    (x) => x.location === 'source'
  );
  remaining = rest1;

  if (source.length > 0) {
    update.source = EraQL.renameSymbolInEraql({
      from,
      to,
      eraql: node.source,
    });
  }

  if (remaining.length > 0) {
    throw Error(
      `Logic error, rel cant handle replacing ${JSON.stringify(remaining)}`
    );
  }

  return update;
};

const documentUpdate = (params: {
  from: string;
  to: string;
  symbol: string;
  node: Graph.DocumentNode;
  deps: readonly Graph.GraphDep[];
}): UpdateDocument => {
  const { from, to, symbol, node } = params;

  const editor = new DocumentEditor.EditorState(node.source);

  // Replace all references in the document source using EditorState method
  const updatedEditor = editor.replaceReference(from, to);

  return {
    symbol,
    t: 'update-document',
    source: updatedEditor.getState(),
  };
};

const agentUpdate = (params: {
  from: string;
  to: string;
  symbol: string;
  node: Graph.AgentNode;
  deps: readonly Graph.GraphDep[];
}): UpdateAgent => {
  const { from, to, symbol, node } = params;

  const editor = new DocumentEditor.EditorState(node.editorState);

  // Replace all references in the agent's editor state using EditorState method
  const updatedEditor = editor.replaceReference(from, to);

  return {
    symbol,
    t: 'update-agent',
    editorState: updatedEditor.getState(),
  };
};

const relUpdate = (params: {
  from: string;
  to: string;
  symbol: string;
  node: Graph.RelNode;
  deps: readonly Graph.GraphDep[];
}): UpdateRel => {
  const { from, to, symbol, node, deps } = params;
  const update: UpdateRel = {
    symbol: symbol,
    t: 'update-rel',
  };

  let remaining = deps;

  const [source, rest1] = _.partition(
    remaining,
    (x) => x.location === 'source'
  );
  remaining = rest1;

  if (source.length > 0) {
    update.source = EraQL.renameSymbolInEraql({
      from,
      to,
      eraql: node.source,
    });
  }

  const [where, rest2] = _.partition(remaining, (x) => x.location === 'where');
  remaining = rest2;

  if (where.length > 0) {
    update.where = EraQL.renameSymbolInEraql({
      from,
      to,
      eraql: node.where!,
    });
  }

  if (remaining.length > 0) {
    throw Error(
      `Logic error, rel cant handle replacing ${JSON.stringify(remaining)}`
    );
  }

  return update;
};

/**
 * Find LLM v2 columns in a relation that reference a renamed symbol/column
 * and create update operations for them.
 */
export const findAndUpdateLlmV2Columns = (params: {
  relSymbol: string;
  relNode: Graph.RelNode;
  from: string;
  to: string;
  isColumnRename: boolean;
  graphExplorer: BuildExplorer;
}): OperationData[] => {
  const { relSymbol, relNode, from, to, graphExplorer } = params;
  const updates: OperationData[] = [];

  for (const [colName, colNode] of Object.entries(relNode.cols)) {
    // Only process LLM v2 columns
    if (colNode.def.t !== 'llm' || colNode.def.version !== 'v2') {
      continue;
    }

    const editorState = colNode.def.editorState;

    const editor = new DocumentEditor.EditorState(editorState);

    // Check if this column's editorState references the renamed symbol/column
    // Use getDependencies() which extracts symbol references from mentions and expressions
    const deps = editor.getDependencies(graphExplorer);
    const needsUpdate = deps.includes(from);

    if (needsUpdate) {
      // Use replaceReference which handles both symbol and column references
      // via EraQL.renameSymbolInEraql in the expression handler
      const updatedEditor = editor.replaceReference(from, to);
      const updatedEditorState = updatedEditor.getState();

      updates.push({
        t: 'update-column',
        relName: relSymbol,
        name: colName,
        config: {
          ...colNode.def,
          editorState: updatedEditorState,
        },
      } as UpdateOrReplaceColumn);
    }
  }

  return updates;
};
